import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IconChevronDown, IconSailboat, IconShare3 } from '@tabler/icons-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as UserCircleIcon } from './react-icons/circleUserIcon.svg';
import { ReactComponent as GearConfigBasicIcon } from './react-icons/gearConfigBasic.svg';
import { ReactComponent as LogoutIcon } from './react-icons/logoutIcon.svg';
import { ReactComponent as PaymentIcon } from './react-icons/paymentIcon.svg';
import { ReactComponent as ProfileIcon } from './react-icons/profileIcon.svg';
import { ReactComponent as SailTrackLogo } from './react-icons/SailTrackLogo.svg';
import { ReactComponent as SecurityIcon } from './react-icons/security.svg';
import { ReactComponent as ToolboxIcon } from './react-icons/toolBox.svg';
import { ReactComponent as UserPrivacyIcon } from './react-icons/userPrivacy.svg';
import { checkIsAdmin } from './services/config';

type ApiResponse = { success: boolean; message?: string };
type UserInfoResponse = ApiResponse & { name?: string };

function isApiResponse(value: unknown): value is ApiResponse {
  if (typeof value !== 'object' || value === null) return false;
  const response = value as Record<string, unknown>;
  return (
    typeof response.success === 'boolean' &&
    (response.message === undefined || typeof response.message === 'string')
  );
}

function isUserInfoResponse(value: unknown): value is UserInfoResponse {
  if (!isApiResponse(value)) {
    return false;
  }
  const response = value as Record<string, unknown>;
  return response.name === undefined || typeof response.name === 'string';
}

function getGreeting(time: Date, lang: 'en' | 'es' | 'it'): string {
  const hour = time.getHours();
  if (hour >= 6 && hour < 12) {
    return lang === 'es' ? 'Buenos días' : lang === 'it' ? 'Buongiorno' : 'Good morning';
  }
  if (hour >= 12 && hour < 20) {
    return lang === 'es' ? 'Buenas tardes' : lang === 'it' ? 'Buon pomeriggio' : 'Good afternoon';
  }
  if (hour >= 20 || hour < 6) {
    return lang === 'es' ? 'Buenas noches' : lang === 'it' ? 'Buonasera' : 'Good evening';
  }
  return lang === 'es' ? 'Hola' : lang === 'it' ? 'Ciao' : 'Hello';
}

export const Sidebar: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: '', greeting: '', avatar: '' });
  const [isAdmin, setIsAdmin] = useState(false);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const accountNavRef = useRef<HTMLElement>(null);
  const avatarObjectUrlRef = useRef<string | null>(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/authUser');
  }, [navigate]);

  const closeMenu = useCallback((restoreFocus = false) => {
    setMenuOpen(false);
    if (restoreFocus) avatarButtonRef.current?.focus();
  }, []);

  const navigateFromMenu = useCallback(
    (path: string) => {
      closeMenu();
      navigate(path);
    },
    [closeMenu, navigate],
  );

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!avatarButtonRef.current?.contains(target) && !accountNavRef.current?.contains(target)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu(true);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  useEffect(() => {
    const controller = new AbortController();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('token');

    const updateGreeting = () => {
      const lower = i18n.language?.toLowerCase();
      const lang = (lower === 'es' ? 'es' : lower === 'it' ? 'it' : 'en') as 'en' | 'es' | 'it';
      setUserInfo((previous) => ({ ...previous, greeting: getGreeting(new Date(), lang) }));
    };

    const fetchUserInfo = async () => {
      if (!baseUrl || !token) return;
      try {
        const response = await axios.get(`${baseUrl}/api/user/getUserInfo`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const payload: unknown = response.data;
        if (isUserInfoResponse(payload) && payload.success) {
          setUserInfo((previous) => ({ ...previous, name: payload.name ?? '' }));
        }
      } catch (error) {
        if (!controller.signal.aborted) console.error('Error fetching user info:', error);
      }
    };

    const fetchAvatar = async () => {
      if (!baseUrl || !token) return;
      try {
        const response = await axios.post(
          `${baseUrl}/api/user/getAvatarFromDB`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
            signal: controller.signal,
          },
        );
        const data: unknown = response.data;
        if (response.status !== 200 || !(data instanceof Blob) || data.size === 0) return;

        if (data.type === 'application/json' || data.type.startsWith('application/json')) {
          const payload: unknown = JSON.parse(await data.text());
          if (isApiResponse(payload) && payload.success) {
            setUserInfo((previous) => ({ ...previous, avatar: '' }));
          }
          return;
        }

        const nextUrl = URL.createObjectURL(data);
        if (avatarObjectUrlRef.current) URL.revokeObjectURL(avatarObjectUrlRef.current);
        avatarObjectUrlRef.current = nextUrl;
        setUserInfo((previous) => ({ ...previous, avatar: nextUrl }));
      } catch (error) {
        if (!controller.signal.aborted) console.error('Error fetching avatar:', error);
      }
    };

    const checkAdminStatus = async () => {
      try {
        const nextIsAdmin = await checkIsAdmin();
        if (!controller.signal.aborted) setIsAdmin(nextIsAdmin);
      } catch {
        if (!controller.signal.aborted) setIsAdmin(false);
      }
    };

    updateGreeting();
    void fetchUserInfo();
    void fetchAvatar();
    void checkAdminStatus();

    const timer = window.setInterval(updateGreeting, 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current);
        avatarObjectUrlRef.current = null;
      }
    };
  }, [i18n.language]);

  return (
    <aside
      className="sidebar-home"
      aria-label={t('nav.workspaceNavigation', 'Workspace navigation')}
    >
      <div className="sidebar-content">
        <button
          type="button"
          className="logo-container-home"
          onClick={() => navigate('/home')}
          aria-label={t('nav.home', 'Home')}
        >
          <SailTrackLogo className="logo-home" aria-hidden="true" focusable="false" />
          <span className="logo-title-home">SailTrack</span>
        </button>

        <div className="line" aria-hidden="true" />

        <div className="user-info">
          <button
            ref={avatarButtonRef}
            type="button"
            className="account-trigger"
            aria-expanded={menuOpen}
            aria-controls="account-navigation"
            aria-label={
              menuOpen
                ? t('nav.closeAccountMenu', 'Close account settings')
                : t('nav.openAccountMenu', 'Open account settings')
            }
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="avatar-container" aria-hidden="true">
              {userInfo.avatar === '' ? (
                <UserCircleIcon className="avatar" focusable="false" />
              ) : (
                <img
                  src={userInfo.avatar}
                  alt=""
                  className="avatar"
                  onError={() => setUserInfo((previous) => ({ ...previous, avatar: '' }))}
                />
              )}
            </span>

            <span className="user-details">
              <span className="name">{userInfo.name || t('nav.account', 'Account')}</span>
              <span className="greeting">{userInfo.greeting}</span>
            </span>

            <IconChevronDown className="account-chevron" aria-hidden="true" stroke={1.8} />
          </button>

          <nav
            ref={accountNavRef}
            id="account-navigation"
            className="dropdown-menu"
            aria-label={t('nav.accountNavigation', 'Account settings')}
            hidden={!menuOpen}
          >
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/profile')}
            >
              <ProfileIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.profile')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/configuration')}
            >
              <GearConfigBasicIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.configuration')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/boats')}
            >
              <IconSailboat className="dropdown-icon" aria-hidden="true" stroke={1.7} />
              {t('nav.boats', 'Boats')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/data-privacy')}
            >
              <UserPrivacyIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.dataPrivacy')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/sharing')}
            >
              <IconShare3 className="dropdown-icon" aria-hidden="true" stroke={1.7} />
              {t('nav.sharing', 'Share data')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/security')}
            >
              <SecurityIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.security')}
            </button>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => navigateFromMenu('/settings/payments')}
            >
              <PaymentIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.paymentsSubscriptions')}
            </button>
            {isAdmin && (
              <button
                type="button"
                className="dropdown-item dropdown-item--admin"
                onClick={() => navigateFromMenu('/admin/crear-usuario')}
              >
                <ToolboxIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
                {t('nav.adminPanel')}
              </button>
            )}
            <button
              type="button"
              className="dropdown-item sidebar-menu-logout"
              onClick={handleLogout}
            >
              <LogoutIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
              {t('nav.logout')}
            </button>
          </nav>
        </div>
      </div>

      <div className="sidebar-footer">
        <button type="button" className="logout-button" onClick={handleLogout}>
          <LogoutIcon className="dropdown-icon" aria-hidden="true" focusable="false" />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
};
